import { useState } from 'react';
import { alertsApi } from '../api/client';
import type { Alert } from '../api/client';
import { T, SectionHeader, Chip, Card, SEV_COLOR, ErrorBox } from '../components/ui';

interface Props {
  alerts: Alert[];
  setAlerts: (fn: (prev: Alert[]) => Alert[]) => void;
}

export default function AlertsPage({ alerts, setAlerts }: Props) {
  const [filter,    setFilter]    = useState<string>('all');
  const [selected,  setSelected]  = useState<Alert | null>(null);
  const [aiRec,     setAiRec]     = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError,   setAiError]   = useState<string | null>(null);

  const unread   = alerts.filter(a => a.status === 'open').length;
  const filtered = filter === 'all' ? alerts : alerts.filter(a => a.severity === filter);
  const sevCounts: Record<string, number> = {};
  for (const a of alerts) sevCounts[a.severity] = (sevCounts[a.severity] || 0) + 1;

  const markRead = async (id: string) => {
    try {
      await alertsApi.markRead(id);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: 'acknowledged' } : a));
    } catch { /* silent - WS will update */ }
  };

  const markAllRead = async () => {
    const openIds = alerts.filter(a => a.status === 'open').map(a => a.id);
    await Promise.allSettled(openIds.map(id => alertsApi.markRead(id)));
    setAlerts(prev => prev.map(a => ({ ...a, status: a.status === 'open' ? 'acknowledged' : a.status })));
  };

  const handleSelect = async (a: Alert) => {
    setSelected(a); setAiRec(null); setAiError(null);
    if (a.status === 'open') markRead(a.id);
  };

  const getRecommendation = async () => {
    if (!selected) return;
    setAiLoading(true); setAiError(null);
    try {
      const data = await alertsApi.recommend(selected.id);
      setAiRec(data.recommendation);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  const resolve = async () => {
    if (!selected) return;
    try {
      await alertsApi.update(selected.id, { status: 'resolved' });
      setAlerts(prev => prev.map(a => a.id === selected.id ? { ...a, status: 'resolved' } : a));
      setSelected(null);
    } catch {}
  };

  const SEVS = ['all', 'critical', 'high', 'medium', 'low', 'info'] as const;

  return (
    <div className="fade-up">
      <SectionHeader
        title="Alert Center"
        sub={`${unread} unread notifications requiring attention`}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {unread > 0 && (
              <button onClick={markAllRead} style={{
                background: T.card, border: `1px solid ${T.border}`, color: T.sub,
                borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 600,
              }}>Mark all read</button>
            )}
            <Chip label={`${unread} Unread`} color={unread > 0 ? T.crimson : T.sage} dot />
          </div>
        }
      />

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {SEVS.map(s => {
          const c = s === 'all' ? T.copper : SEV_COLOR[s] || T.sub;
          const cnt = s === 'all' ? alerts.length : (sevCounts[s] || 0);
          return (
            <button key={s} onClick={() => setFilter(s)} style={{
              background: filter === s ? `${c}18` : T.panel,
              border: `1px solid ${filter === s ? c + '50' : T.border}`,
              color: filter === s ? c : T.sub,
              borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 11, fontWeight: filter === s ? 700 : 400,
            }}>
              {s.charAt(0).toUpperCase() + s.slice(1)} {cnt > 0 && `(${cnt})`}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Alert list */}
        <div>
          {filtered.length === 0
            ? <div style={{ color: T.sub, fontSize: 12, textAlign: 'center', padding: '40px 0' }}>No alerts in this category</div>
            : filtered.map(a => (
            <div key={a.id} onClick={() => handleSelect(a)} style={{
              background: selected?.id === a.id ? T.lift : T.panel,
              border: `1px solid ${selected?.id === a.id ? SEV_COLOR[a.severity] + '50' : T.border}`,
              borderLeft: `3px solid ${SEV_COLOR[a.severity] || T.sub}`,
              borderRadius: 10, padding: '13px 15px', marginBottom: 8,
              cursor: 'pointer', transition: 'all .12s',
              opacity: a.status !== 'open' ? 0.7 : 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {a.status === 'open' && <div style={{ width: 7, height: 7, borderRadius: '50%', background: SEV_COLOR[a.severity], flexShrink: 0, marginTop: 1 }} />}
                  <span style={{ fontSize: 12, fontWeight: a.status === 'open' ? 600 : 400, color: T.text }}>{a.title}</span>
                </div>
                <Chip label={a.severity} color={SEV_COLOR[a.severity] || T.sub} />
              </div>
              <div style={{ fontSize: 10, color: T.sub, paddingLeft: a.status === 'open' ? 15 : 0 }}>
                {a.product_name && `${a.product_name}  ·  `}
                {new Date(a.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>

        {/* Detail panel */}
        <div>
          {!selected
            ? (
              <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🔔</div>
                <div style={{ color: T.sub, fontSize: 12 }}>Select an alert to view details and AI recommendation</div>
              </Card>
            )
            : (
              <Card style={{ borderLeft: `3px solid ${SEV_COLOR[selected.severity] || T.sub}` }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                    <Chip label={selected.severity} color={SEV_COLOR[selected.severity] || T.sub} />
                    {selected.status !== 'open' && <Chip label={selected.status} color={T.sub} />}
                  </div>
                  <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 17, color: T.text, marginBottom: 8 }}>{selected.title}</div>
                  <div style={{ color: T.sub, fontSize: 12, lineHeight: 1.6 }}>{selected.detail}</div>
                </div>

                {selected.product_name && (
                  <div style={{ background: T.card, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 11, color: T.sub }}>
                    Product: <span style={{ color: T.text }}>{selected.product_name}</span>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <button onClick={getRecommendation} disabled={aiLoading} style={{
                    flex: 1, background: aiLoading ? T.border : `${T.copper}20`,
                    border: `1px solid ${T.copper}40`, color: aiLoading ? T.sub : T.copper,
                    borderRadius: 8, padding: '9px', cursor: aiLoading ? 'not-allowed' : 'pointer',
                    fontSize: 11, fontWeight: 600,
                  }}>
                    {aiLoading ? 'Generating…' : aiRec ? 'Regenerate AI Analysis' : '✦ Get AI Recommendation'}
                  </button>
                  {selected.status === 'open' && (
                    <button onClick={resolve} style={{
                      background: `${T.sage}15`, border: `1px solid ${T.sage}40`,
                      color: T.sage, borderRadius: 8, padding: '9px 14px',
                      cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    }}>Resolve</button>
                  )}
                </div>

                {aiError && <ErrorBox message={aiError} />}

                {aiRec && (
                  <div style={{
                    background: `${T.copper}08`, border: `1px solid ${T.copper}25`,
                    borderRadius: 9, padding: '14px 16px',
                  }}>
                    <div style={{ color: T.copper, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 10 }}>
                      AI Recommendation
                    </div>
                    <div style={{ color: T.sub, fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{aiRec}</div>
                  </div>
                )}
              </Card>
            )
          }
        </div>
      </div>
    </div>
  );
}
